
import React, { useState, useMemo, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import ProductCard from '../components/ProductCard';
import { PRODUCTS, CATEGORY_MAP } from '../constants';

const Shop: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  
const initialCategory = searchParams.get('category') || 'Todo';
  const initialCollection = searchParams.get('collection') || 'Todo';
  const initialBrand = searchParams.get('brand') || 'Todo';
  
  const [categoryFilter, setCategoryFilter] = useState<string>(initialCategory);
  const [collectionFilter, setCollectionFilter] = useState<string>(initialCollection);
  const [brandFilter, setBrandFilter] = useState<string>(initialBrand);
  const [sortBy, setSortBy] = useState<string>('Destacados');

  const categories = ['Todo', 'Hoodies',  'Sudaderas sin Capucha', 'T-Shirts', 'Tazas', 'Botellas', 'Tote bag',];
  const collections = ['Todo', 'MINIMAL', 'GRAPHIC', 'SANAN'];
  const brands = ['Todo', ...new Set(PRODUCTS.map(p => p.brand))]; // Marcas únicas de los productos

  // Sincronizar filtros si la URL cambia (ej: navegación externa)
  useEffect(() => {
    const cat = searchParams.get('category') || 'Todo';
    const col = searchParams.get('collection') || 'Todo';
    const brd = searchParams.get('brand') || 'Todo';
    setCategoryFilter(cat);
    setCollectionFilter(col);
    setBrandFilter(brd);
  }, [searchParams]);

  const updateFilters = (newCat?: string, newCol?: string, newBrd?: string) => {
    const finalCat = newCat !== undefined ? newCat : categoryFilter;
    const finalCol = newCol !== undefined ? newCol : collectionFilter;
    const finalBrd = newBrd !== undefined ? newBrd : brandFilter;

    setCategoryFilter(finalCat);
    setCollectionFilter(finalCol);
    setBrandFilter(finalBrd);

    const params = new URLSearchParams();
    if (finalCat !== 'Todo') params.set('category', finalCat);
    if (finalCol !== 'Todo') params.set('collection', finalCol);
    if (finalBrd !== 'Todo') params.set('brand', finalBrd);
    setSearchParams(params);
  };

  const filteredProducts = useMemo(() => {
    let result = PRODUCTS;

    // Filtro por Colección
    if (collectionFilter !== 'Todo') {
      result = result.filter(p => p.collection === collectionFilter);
    }

    // Filtro por Categoría
    if (categoryFilter !== 'Todo') {
      result = result.filter(p => p.category === categoryFilter);
    }

    // Filtro por Marca (nuevo)
    if (brandFilter !== 'Todo') {
      result = result.filter(p => p.brand === brandFilter);
    }
    
    // Ordenación
    if (sortBy === 'Precio: Menor a Mayor') {
      return [...result].sort((a, b) => a.price - b.price);
    } else if (sortBy === 'Precio: Mayor a Menor') {
      return [...result].sort((a, b) => b.price - a.price);
    }
    return result;
  }, [categoryFilter, collectionFilter, brandFilter, sortBy]);

  return (
    <div className="flex flex-col items-center w-full px-4 md:px-10 py-16 max-w-[1400px] mx-auto">
<section className="w-full bg-black py-3 px-4 md:px-10 mb-10 rounded-xl">
      <div className="max-w-[1400px] mx-auto text-center">
        <p className="text-white text-base md:text-lg font-bold uppercase tracking-widest italic flex items-center justify-center gap-3">
          <span className="material-symbols-outlined text-xl md:text-2xl">local_shipping</span>
          Envío GRATIS en pedidos a partir de 65€
          <span className="material-symbols-outlined text-xl md:text-2xl">local_shipping</span>
        </p>
      </div>
    </section>
      <div className="w-full mb-12">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-8 mb-12">
          <div>
            <h1 className="text-5xl md:text-7xl font-black tracking-tighter uppercase italic">La Tienda</h1>
          </div>
          <div className="flex items-center gap-4 text-sm font-bold text-gray-400 italic">
            <span className="size-2 bg-green-500 rounded-full animate-pulse"></span>
            Filtros Activos: {collectionFilter} / {CATEGORY_MAP[categoryFilter] || categoryFilter}
          </div>
        </div>
        
        <div className="flex flex-col gap-6 border-y border-gray-100 py-10">
          {/* Fila 1: Colecciones */}
          <div className="flex flex-col gap-4">
            <span className="text-[10px] font-black uppercase tracking-[0.3em] text-gray-400 italic ml-1">Seleccionar Línea</span>
            <div className="flex items-center gap-3 overflow-x-auto no-scrollbar">
              {collections.map(col => (
                <button 
                  key={col}
                  onClick={() => updateFilters(undefined, col)}
                  className={`flex h-11 shrink-0 items-center justify-center rounded-xl px-10 transition-all font-black text-xs uppercase tracking-widest italic border ${
                    collectionFilter === col 
                    ? 'bg-primary border-primary text-white shadow-xl shadow-primary/20 scale-100' 
                    : 'bg-white border-gray-100 text-gray-400 hover:border-gray-300 hover:text-black'
                  }`}
                >
                  {col}
                </button>
              ))}
            </div>
          </div>

          {/* Fila 2: Categorías */}
          <div className="flex flex-col gap-4 mt-4">
            <span className="text-[10px] font-black uppercase tracking-[0.3em] text-gray-400 italic ml-1">Tipo de Artículo</span>
            <div className="flex items-center gap-3 overflow-x-auto no-scrollbar">
              {categories.map(cat => (
                <button 
                  key={cat}
                  onClick={() => updateFilters(cat, undefined)}
                  className={`flex h-10 shrink-0 items-center justify-center gap-x-2 rounded-lg px-8 transition-all font-bold text-[10px] uppercase tracking-widest italic ${
                    categoryFilter === cat 
                    ? 'bg-black text-white' 
                    : 'bg-gray-50 border border-transparent text-gray-500 hover:bg-gray-100'
                  }`}
                >
                  {CATEGORY_MAP[cat] || cat}
                </button>
              ))}
            </div>
          </div>

          {/* Fila 3: Ordenación */}
<div className="flex flex-col md:flex-row md:items-center justify-between gap-6 pt-6 border-t border-gray-50 mt-4">
            <div className="flex items-center gap-6">
              <div className="flex items-center gap-3">
                <span className="text-gray-400 text-[10px] font-black uppercase tracking-widest">Marca</span>
                <select 
                  value={brandFilter}
                  onChange={(e) => updateFilters(undefined, undefined, e.target.value)}
                  className="bg-transparent text-black text-sm font-bold uppercase italic border-none focus:ring-0 cursor-pointer"
                >
                  {brands.map(brand => (
                    <option key={brand}>{brand}</option>
                  ))}
                </select>
              </div>
              <div className="flex items-center gap-3">
                <span className="text-gray-400 text-[10px] font-black uppercase tracking-widest">Ordenar por</span>
                <select 
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  className="bg-transparent text-black text-sm font-bold uppercase italic border-none focus:ring-0 cursor-pointer"
                >
                  <option>Destacados</option>
                  <option>Precio: Menor a Mayor</option>
                  <option>Precio: Mayor a Menor</option>
                </select>
              </div>
              <div className="hidden sm:block text-xs font-bold text-gray-500 uppercase tracking-widest">
  {filteredProducts.length} Resultados
</div>
            </div>
            
            {(categoryFilter !== 'Todo' || collectionFilter !== 'Todo' || brandFilter !== 'Todo') && (
              <button 
                onClick={() => updateFilters('Todo', 'Todo', 'Todo')}
                className="text-primary text-[10px] font-black uppercase tracking-widest hover:underline flex items-center gap-2"
              >
                <span className="material-symbols-outlined text-sm">close</span>
                Limpiar Filtros
              </button>
            )}
          </div>
        </div>
      </div>

{filteredProducts.length > 0 ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-x-8 gap-y-16 w-full">
          {filteredProducts.map(product => (
            <ProductCard key={product.id} product={product} />
          ))}
        </div>
      ) : (
        <div className="flex flex-col items-center justify-center py-20 gap-4 opacity-40">
           <span className="material-symbols-outlined text-6xl">search_off</span>
           <p className="font-bold uppercase tracking-[0.2em] text-sm italic">No se encontraron artículos con esta combinación</p>
           <button onClick={() => updateFilters('Todo', 'Todo', 'Todo')} className="text-primary font-black uppercase text-xs hover:underline">Reiniciar búsqueda</button>
        </div>
      )}
    </div>
  );
};

export default Shop;